// IntradayBubbleMap.jsx
import React, { useEffect, useMemo, useState } from "react";
import Plot from "react-plotly.js";
import {fetchTable} from "../../api";

const API_URL = "/api/intraday/bubble/?window=5m&limit=200"; // adjust as needed

function scaleArray(vals, outMin = 8, outMax = 60) {
  const vmin = Math.min(...vals);
  const vmax = Math.max(...vals);
  if (!isFinite(vmin) || !isFinite(vmax) || vmax === vmin) {
    return vals.map(() => (outMin + outMax) / 2);
  }
  return vals.map(v => outMin + (outMax - outMin) * ((v - vmin) / (vmax - vmin)));
}

export default function IntradayBubbleMap({ onPointClick }) {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [useLogY, setUseLogY] = useState(true);

  useEffect(() => {
    fetchTable("intraday_pv",'n50')
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(json => {
        setRows(Array.isArray(json) ? json : []);
        setLoading(false);
      })
      .catch(e => {
        setErr(e.message);
        setLoading(false);
      });
  }, []);

  const dataReady = useMemo(() => {
    if (!rows.length) return null;

    const symbols = rows.map(r => r.symbol);
    const price = rows.map(r => Number(r.last_price) || 0);
    const vol = rows.map(r => Number(r.computed_volume) || 0);
    const chg = rows.map(r => Number(r.change_percent) || 0);

    // inside dataReady, after computing symbols, price, vol, chg, sizes
    const topN = 10;
    // Sort indices by volume (or size)
    const indices = vol
      .map((v, i) => [i, v])
      .sort((a, b) => b[1] - a[1]) // descending volume
      .slice(0, topN)
      .map(d => d[0]);

    const labels = symbols.map((s, i) =>
      indices.includes(i) ? s : "" // label only topN
    );

    // Size: log-compressed volume → scaled to 8..60 px diameter
    const volLog = vol.map(v => Math.log10((v || 0) + 1));
    const sizes = scaleArray(volLog, 8, 60);

    return {
      symbols, price, vol, chg, sizes, labels
    };
  }, [rows]);

  if (loading) return <div>Loading bubble map…</div>;
  if (err) return <div style={{ color: "crimson" }}>Error: {err}</div>;
  if (!dataReady) return <div>No data</div>;

  const { symbols, price, vol, chg, sizes,labels } = dataReady;

  return (
    <div>
      <div style={{ marginBottom: 10, display: "flex", gap: 12, alignItems: "center" }}>
        <strong>   Intraday Price vs Volume</strong>
        <label style={{ fontSize: 12 }}>
          <input type="checkbox" checked={useLogY} onChange={e => setUseLogY(e.target.checked)} />
          &nbsp;Log scale volume
        </label>
      </div>

      <Plot
        data={[
          {
            type: "scattergl",
            mode: "markers+text",   // <-- markers with optional labels
            x: price,
            y: vol,
            text: labels,           // <-- only top 10 will have text
            textposition: "top center",
            customdata: symbols.map((s, i) => ({
              symbol: s,
              last_price: price[i],
              volume: vol[i],
              change: chg[i],
            })),
            hovertemplate:
              "<b>%{customdata.symbol}</b><br>" +
              "Price: %{customdata.last_price:.2f}<br>" +
              "Volume: %{customdata.volume:,}<br>" +
              "Change: %{customdata.change:.2f}%<extra></extra>",
            marker: {
              size: sizes,
              sizemode: "diameter",
              color: chg,
              colorscale: [
              [0, "red"],
              [0.5, "yellow"],
              [1, "green"]
              ],
              cmin: Math.min(...chg),
              cmax: Math.max(...chg),
              line: { width: 0.5, color: "rgba(0,0,0,0.3)" },
              colorbar: { title: "Change %" },
              opacity: 0.9,
            },
          },
        ]}
        layout={{
          height: 500,
          width: 550,
          margin: { l: 60, r: 20, t: 30, b: 60 },
          xaxis: { title: "Last Price", zeroline: false, gridcolor: "#eee" },
          yaxis: {
            title: "Computed Volume",
            type: useLogY ? "log" : "linear",
            zeroline: false,
            gridcolor: "#eee",
          },

          dragmode: "pan",
          hovermode: "closest",
        }}
        onClick={(ev) => {
          const pt = ev?.points?.[0]?.customdata;
          if (pt && onPointClick) onPointClick(pt.symbol);
        }}
        config={{ displayModeBar: false, responsive: true }}
        style={{ width: "100%" }}
      />
    </div>
  );
}
